import React, { useState, useContext, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../../../utils/numberFormatter';
import { requiresAdvancePayment } from '../../../../utils/advancePayment';
import PaymentMethods from '../../../PaymentFlow/PaymentMethods';
import { SelectedOfferContext } from '../../../../context/SelectedOfferContext';
import api from '../../../../services/api';
import Swal from 'sweetalert2';

const PaymentModal = ({ 
  show, 
  onClose, 
  onConfirm, 
  isProcessing = false,
  clientData,
  purchaseRequestId,
  purchaseRequestUid,
}) => {
  const { selectedOffer } = useContext(SelectedOfferContext);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transferReceipt, setTransferReceipt] = useState(null);
  const [widgetError, setWidgetError] = useState('');
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [isWaitingWebhook, setIsWaitingWebhook] = useState(false);
  const [isDebitPaymentReady, setIsDebitPaymentReady] = useState(false);
  const [debitPaymentUid, setDebitPaymentUid] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const paymentWidgetRef = useRef(null);
  const hasTriggeredConfirmationRef = useRef(false);
  const pollingRunRef = useRef(0);

  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;
  const widgetDocument = clientData?.cuit || clientData?.dni || '';
  const isDebitWebhookPending = paymentMethod === 'debit_card' && isWaitingWebhook;
  const isClosingDisabled = isProcessing || isDebitWebhookPending;

  useEffect(() => {
    if (!show) {
      pollingRunRef.current += 1;
      setPaymentMethod('cash');
      setTransferReceipt(null);
      setWidgetError('');
      setIsWidgetLoading(false);
      setIsWaitingWebhook(false);
      setIsDebitPaymentReady(false);
      setDebitPaymentUid(null);
      setReloadTrigger(0);
      hasTriggeredConfirmationRef.current = false;
    }
  }, [show]);

  useEffect(() => {
    if (!show || paymentMethod !== 'debit_card') {
      return undefined;
    }

    if (!widgetDocument || !purchaseRequestId) {
      setWidgetError('Faltan datos para iniciar el pago con tarjeta de débito.');
      return undefined;
    }

    pollingRunRef.current += 1;
    const currentRun = pollingRunRef.current;
    let isActive = true;
    let paymentsInstance = null;
    const widgetContainer = document.getElementById('cirenioDownpaymentContainer');
    if (widgetContainer) {
      widgetContainer.innerHTML = '';
    }

    setWidgetError('');
    setIsWidgetLoading(true);

    const initializeWidget = () => {
      if (!isActive || !window.CirenioPayments) {
        return;
      }

      paymentsInstance = new window.CirenioPayments(
        publicKey,
        widgetDocument,
        {
          containerId: 'cirenioDownpaymentContainer',
          externalId: `purchase_request:${purchaseRequestUid || purchaseRequestId}`,
          flowType: 'payment',
          advancePayment: {
            amount: Number(selectedOffer?.downpaymentAmount || 0),
          },
          paymentConcept: selectedOffer?.productName || 'Pago inicial de la compra',
          paymentDescription: 'Pago inicial con tarjeta de débito',
          firstName: clientData?.firstName,
          lastName: clientData?.lastName,
          email: clientData?.email,
          phone: clientData?.phone || clientData?.full_phone || undefined,
          subjectType: 'purchase_request',
          subjectId: String(purchaseRequestId),
          subjectUid: purchaseRequestUid || undefined,
          paymentContext: 'downpayment_debit_card',
        }
      );

      paymentsInstance.on('widget_start', () => {
        if (isActive) {
          setIsWidgetLoading(false);
        }
      });

      paymentsInstance.on('tokenization_success', async (cardData) => {
        if (!isActive || currentRun !== pollingRunRef.current) return;

        const cardHolderId = cardData?.source?.holder?.identification;

        if (cardHolderId && clientData?.dni && cardHolderId !== clientData.dni) {
          await Swal.fire({
            icon: 'error',
            title: 'La tarjeta debe estar a nombre del cliente',
            text: 'El titular de la tarjeta no coincide con el DNI del cliente. Intentá nuevamente con otra tarjeta.',
            confirmButtonText: 'Entendido',
          });
          reloadWidget();
          return;
        }

        try {
          setIsWaitingWebhook(true);
          const paymentStatus = await pollPaymentStatus(currentRun);
          if (!isActive || currentRun !== pollingRunRef.current) {
            return;
          }

          const paymentUid = paymentStatus?.downpayment?.payment_uid || null;
          setIsWaitingWebhook(false);
          setIsDebitPaymentReady(true);
          setDebitPaymentUid(paymentUid);

          if (!hasTriggeredConfirmationRef.current) {
            await triggerDebitConfirmation({
              requiresAdvance: true,
              payment_method: 'debit_card',
              payment_uid: paymentUid,
            });
          }
        } catch (error) {
          if (!isActive || currentRun !== pollingRunRef.current) {
            return;
          }

          setIsWaitingWebhook(false);
          setWidgetError(error.message || 'Registramos el pago, pero todavía no lo confirmamos. Intentá de nuevo en unos segundos.');
        }
      });

      paymentsInstance.on('tokenization_error', () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setWidgetError('Hubo un problema al procesar el pago con tarjeta. Intentá de nuevo.');
        }
      });

      paymentsInstance.openWidget();
    };

    let script = null;

    if (window.CirenioPayments) {
      initializeWidget();
    } else {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = initializeWidget;
      script.onerror = () => {
        setIsWidgetLoading(false);
        setWidgetError('No pudimos cargar la pantalla de pago. Revisá tu conexión e intentá de nuevo.');
      };
      document.body.appendChild(script);
    }

    return () => {
      isActive = false;
      pollingRunRef.current += 1;
      if (paymentsInstance) {
        const cleanupMethod = paymentsInstance.destroy || paymentsInstance.close || paymentsInstance.unmount;
        if (cleanupMethod) {
          cleanupMethod.call(paymentsInstance);
        }
      }
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (widgetContainer) {
        widgetContainer.innerHTML = '';
      }
    };
  }, [
    show,
    paymentMethod,
    publicKey,
    scriptSrc,
    widgetDocument,
    purchaseRequestId,
    reloadTrigger,
    selectedOffer?.downpaymentAmount,
    selectedOffer?.productName,
    clientData?.dni,
    clientData?.firstName,
    clientData?.lastName,
    clientData?.email,
    clientData?.phone,
    purchaseRequestUid,
  ]);

  const triggerDebitConfirmation = async (paymentData) => {
    hasTriggeredConfirmationRef.current = true;

    try {
      const confirmationResult = await Promise.resolve(onConfirm(paymentData));

      if (confirmationResult === false) {
        hasTriggeredConfirmationRef.current = false;
        setWidgetError('Confirmamos el pago, pero no pudimos crear la venta. Revisá el error e intentá de nuevo.');
        return false;
      }

      return true;
    } catch (error) {
      hasTriggeredConfirmationRef.current = false;
      setWidgetError(error.message || 'Confirmamos el pago, pero no pudimos crear la venta. Intentá de nuevo.');
      return false;
    }
  };

  const handleConfirmPayment = () => {
    if (!paymentMethod) return;

    if (paymentMethod === 'debit_card') {
      if (isDebitPaymentReady && !hasTriggeredConfirmationRef.current) {
        triggerDebitConfirmation({
          requiresAdvance: true,
          payment_method: 'debit_card',
          payment_uid: debitPaymentUid,
        });
      }
      return;
    }

    const paymentData = {
      requiresAdvance: true,
      payment_method: paymentMethod,
      amount: selectedOffer?.downpaymentAmount || 0,
      voucher: transferReceipt
    };
    onConfirm(paymentData);
  };

  const handleFileChange = (e) => {
    setTransferReceipt(e.target.files[0]);
  };

  const reloadWidget = () => {
    pollingRunRef.current += 1;
    hasTriggeredConfirmationRef.current = false;
    setWidgetError('');
    setIsWidgetLoading(true);
    setIsWaitingWebhook(false);
    setIsDebitPaymentReady(false);
    setDebitPaymentUid(null);
    setReloadTrigger((prev) => prev + 1);
  };

  const pollPaymentStatus = async (runId) => {
    const maxAttempts = 12;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (runId !== pollingRunRef.current) {
        throw new Error('Polling cancelled');
      }

      const response = await api.get(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/payment-status`
      );

      if (runId !== pollingRunRef.current) {
        throw new Error('Polling cancelled');
      }

      if (response.data?.downpayment?.ready) {
        return response.data;
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    throw new Error('Registramos el pago, pero todavía no lo confirmamos. Intentá de nuevo en unos segundos.');
  };

  return (
    <Modal
      show={show}
      onHide={isClosingDisabled ? undefined : onClose}
      size="lg"
      backdrop={isClosingDisabled ? 'static' : true}
      keyboard={!isClosingDisabled}
    >
      <Modal.Header closeButton={!isClosingDisabled}>
        <Modal.Title>Cobrá el pago inicial</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Resumen del Pago */}
        <div className="mb-0">
          <div className="card shadow-sm border-0 text-center mb-2">
            <div className="card-header bg-white border-0 pb-0">
              <FontAwesomeIcon icon={faHandHoldingUsd} className="me-2" />
              <strong>Importe a pagar</strong>
            </div>
            <div className="card-body pt-2 text-center">
              <span
                style={{
                  fontSize: '2.5rem',
                  color: '#1976d2', // Azul Material UI
                  fontWeight: 700,
                  letterSpacing: '1px',
                  display: 'block',
                }}
              >
                {formatCurrency(selectedOffer?.downpaymentAmount || 0, 2)}
              </span>
            </div>
          </div>
        </div>

        {/* Selección de medio de pago y comprobante */}
        <div className="mb-0">
          <div className="card shadow-sm border-0 text-center mb-3">
            <div className="card-header bg-white border-0 pb-0">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              <strong>Elegí cómo cobrar</strong>
            </div>
            <div className="card-body pt-0">
              <div className="mb-3">
                <PaymentMethods
                  selectedMethod={paymentMethod}
                  onMethodSelect={(method) => {
                    pollingRunRef.current += 1;
                    setPaymentMethod(method);
                    setTransferReceipt(null);
                    setWidgetError('');
                    setIsWaitingWebhook(false);
                    setIsDebitPaymentReady(false);
                    setDebitPaymentUid(null);
                    hasTriggeredConfirmationRef.current = false;
                  }}
                />
                {paymentMethod && (
                  <div className="text-center mt-2 mb-2">
                    <span className="" style={{ fontSize: '1rem', padding: '0.5em 1.2em' }}>
                      {paymentMethod === 'cash' && 'Efectivo'}
                      {paymentMethod === 'bank_transfer' && 'Transferencia'}
                      {paymentMethod === 'debit_card' && 'Tarjeta de débito'}
                    </span>
                  </div>
                )}
              </div>
              {paymentMethod === 'bank_transfer' && (
                <div className="mt-3">
                  <label className="form-label fw-semibold" htmlFor="transferReceipt">
                    Comprobante de transferencia
                  </label>
                  <div
                    className={`transfer-receipt-dropzone ${transferReceipt ? 'uploaded' : ''}`}
                    onClick={() => document.getElementById('transferReceipt').click()}
                    style={{ 
                      userSelect: 'none',
                      border: '2px dashed #dee2e6',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: transferReceipt ? '#f8f9fa' : 'white',
                      width: '100%'
                    }}
                  >
                    <span className="transfer-receipt-icon">
                      <i className="bi bi-file-earmark-text fs-4"></i>
                    </span>
                    <div className="transfer-receipt-name flex-grow-1 mt-2">
                      {transferReceipt ? transferReceipt.name : 'Arrastrá el archivo acá o hacé clic para elegirlo'}
                    </div>
                    <span className="transfer-receipt-status mt-2">
                      {transferReceipt ? (
                        <i className="bi bi-check-circle-fill text-success"></i>
                      ) : (
                        <i className="bi bi-clock"></i>
                      )}
                    </span>
                    <input
                      type="file"
                      id="transferReceipt"
                      accept="image/*,application/pdf"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              )}

              {paymentMethod === 'debit_card' && (
                <div className="mt-3">
                  <div className="alert alert-info text-start mb-3">
                    Completá el pago inicial en la pantalla segura. Cuando lo confirmemos, creamos la venta automáticamente.
                  </div>

                  {isWaitingWebhook && (
                    <div className="alert alert-warning text-start mb-3">
                      No cierres esta ventana mientras confirmamos el pago. El monto ya puede haberse debitado y necesitamos terminar la venta.
                    </div>
                  )}

                  {widgetError && (
                    <div className="alert alert-warning">
                      <div className="mb-2">{widgetError}</div>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={isDebitPaymentReady ? handleConfirmPayment : reloadWidget}
                        disabled={isWaitingWebhook || isProcessing}
                      >
                        {isDebitPaymentReady ? 'Intentar crear la venta' : 'Reintentar pago'}
                      </button>
                    </div>
                  )}

                  {(isWidgetLoading || isWaitingWebhook) && (
                    <div className="text-center py-3">
                      <div className="spinner-border text-primary mb-2" role="status">
                        <span className="visually-hidden">Procesando...</span>
                      </div>
                      <div className="text-muted">
                        {isWaitingWebhook ? 'Confirmando el pago...' : 'Cargando pantalla de pago...'}
                      </div>
                    </div>
                  )}

                  <div
                    id="cirenioDownpaymentContainer"
                    ref={paymentWidgetRef}
                    className="payment-widget"
                    style={{
                      minHeight: '520px',
                      display: isWaitingWebhook ? 'none' : 'block',
                    }}
                  />
                </div>
              )}

              {/* Nuevo bloque de información de anticipo */}
              {requiresAdvancePayment(selectedOffer) && (
                <div className="mt-3 text-start p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div className="d-flex align-items-start">
                    <div>
                      <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                      Para confirmar esta venta, el cliente paga un monto inicial equivalente a{' '}
                      {selectedOffer.downpaymentType === 'percentage' ? (
                          <span className="text-primary">el {selectedOffer.downpaymentValue}% del monto</span>
                        ) : (
                          <span className="text-primary">{selectedOffer.downpaymentValue} cuota{selectedOffer.downpaymentValue > 1 ? 's' : ''}</span>
                        )} en este momento.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
       <div className="d-flex justify-content-between w-100">
        <Button variant="secondary" onClick={onClose} disabled={isClosingDisabled}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirmPayment}
          disabled={isProcessing || !paymentMethod || (paymentMethod === 'debit_card' && !isDebitPaymentReady)}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Procesando...
            </>
          ) : paymentMethod === 'debit_card' ? (
            <>
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              {isDebitPaymentReady ? 'Crear la venta' : (isWaitingWebhook ? 'Confirmando pago...' : 'Completá el pago con tarjeta')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faHandHoldingUsd} className="me-2" />
              Crear la venta y registrar el pago
            </>
          )}
        </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentModal;